import { useEffect, useSyncExternalStore } from "react";
import { axiosInstance } from "@/resources/requestInfo";
import { getTokenFromStorage } from "@/utils";
import { moscowNow } from "@utils/date/moscowNow";

/**
 * Граница продажи кортов (GET /booking-window). Обычным — до конца следующего
 * месяца, тренерам с ранним доступом следующий месяц открывается на день
 * раньше. Продажа открывается в 22:00 по Москве накануне (П1345). Пока ответа
 * нет / бэк недоступен — локальный расчёт по тому же правилу.
 * Возвращает Date (полночь последнего доступного дня, «московские» поля).
 */
type BookingWindowResponse = {
  maxDate: string;
  earlyAccess: boolean;
  nextOpeningAt: string;
};

type State = { maxDate: string | null; earlyAccess: boolean; token: string | null | undefined };

const OPENING_LEAD_HOURS = 2; // 22:00 накануне = полночь минус два часа

const localMaxDate = (): Date => {
  const d = moscowNow();
  d.setHours(d.getHours() + OPENING_LEAD_HOURS);
  // Последний день следующего месяца через день 0 — без setMonth: тот
  // переполнялся 31-го (31.07 + 2 месяца = «31.09» = 01.10) и открывал
  // месяц на день раньше.
  return new Date(d.getFullYear(), d.getMonth() + 2, 0);
};

const parseYmd = (ymd: string): Date => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
};

let state: State = { maxDate: null, earlyAccess: false, token: undefined };
const listeners = new Set<() => void>();
let inflight: Promise<void> | null = null;
let openingTimer: ReturnType<typeof setTimeout> | undefined;

const emit = (next: State) => {
  state = next;
  listeners.forEach((l) => l());
};

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

const fetchWindow = (token: string | null): Promise<void> => {
  if (inflight && state.token === token) return inflight;
  inflight = axiosInstance
    .get<BookingWindowResponse>("/booking-window", { timeout: 10000 })
    .then(({ data }) => {
      if (!data?.maxDate) return;
      emit({ maxDate: data.maxDate, earlyAccess: !!data.earlyAccess, token });
      // Вкладка открыта в момент открытия продаж — перезапросить окно
      clearTimeout(openingTimer);
      const ms = Date.parse(data.nextOpeningAt) - Date.now();
      if (Number.isFinite(ms) && ms > 0 && ms <= 24 * 60 * 60 * 1000) {
        openingTimer = setTimeout(() => fetchWindow(token), ms + 2000);
      }
    })
    .catch(() => {})
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

export const useBookingWindow = (): { maxDate: Date; earlyAccess: boolean } => {
  const snapshot = useSyncExternalStore(subscribe, () => state);
  const token = getTokenFromStorage() || null;

  useEffect(() => {
    if (state.token !== token) {
      emit({ maxDate: null, earlyAccess: false, token });
    }
    fetchWindow(token);
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchWindow(token);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [token]);

  return {
    maxDate: snapshot.maxDate ? parseYmd(snapshot.maxDate) : localMaxDate(),
    earlyAccess: snapshot.earlyAccess,
  };
};
